import { useState } from "react";
import { Eye, EyeOff, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { IronDivider } from "@/components/common/Ornament";

const BG = "https://images.unsplash.com/photo-1640583430339-f83d93e77413?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function LoginPage() {
  const { login } = useAuth();
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    setError("");
    try {
      await login(pw);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F6F0]" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={BG} alt="French Quarter balconies" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F2E23] via-[#0F2E23]/70 to-[#0F2E23]/30" />
        <div className="absolute bottom-0 left-0 p-14 text-[#F9F6F0] max-w-xl">
          <p className="eyebrow !text-[#D4AF37]">Private Investor Presentation</p>
          <h1 className="font-display text-5xl xl:text-6xl font-semibold leading-[1.05] mt-4">Tempo by Hilton<br />S. Rampart Street</h1>
          <p className="mt-5 text-[#F9F6F0]/75 leading-relaxed">A 175-key new-construction hotel in downtown New Orleans — minutes from the Superdome, the Convention Center and the French Quarter.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="login-form">
          <div className="h-12 w-12 rounded-full iron-pattern flex items-center justify-center mb-8">
            <Lock className="h-5 w-5 text-[#D4AF37]" />
          </div>
          <p className="eyebrow">Confidential · Authorized Access Only</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#0F2E23] mt-3">Welcome</h2>
          <p className="text-slate-600 mt-3 text-sm sm:text-base">Enter the viewer password or admin passcode you were given.</p>
          <IronDivider className="my-8" />
          <label htmlFor="pw" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#3E5C4E]">Password / Passcode</label>
          <div className="relative mt-2">
            <input
              id="pw" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus autoComplete="current-password"
              className="w-full h-12 rounded-md border border-[#E2DCD2] bg-white px-4 pr-12 text-base focus:outline-none focus:ring-2 focus:ring-[#C59B27]/50 focus:border-[#C59B27]"
              data-testid="login-passcode-input"
            />
            <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#0F2E23]" data-testid="login-toggle-visibility" aria-label="Toggle visibility">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {error && <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="login-error-msg">{error}</p>}
          <button
            type="submit" disabled={busy || !pw}
            className="mt-6 w-full h-12 rounded-md bg-[#0F2E23] text-[#F9F6F0] font-semibold tracking-wide hover:bg-[#164032] disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            data-testid="login-submit-btn"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Enter Presentation
          </button>
          <p className="mt-6 text-xs text-slate-500 leading-relaxed">Access is logged. Sessions expire after 8 hours of inactivity. After 5 failed attempts, access is locked for 15 minutes.</p>
        </form>
      </div>
    </div>
  );
}
